/**
 * useWhatsNew Hook
 * Manages "What's New" modal state and version history
 * Tracks which versions the user has seen using the new version history system
 */

import { useState, useEffect } from 'react';
import type { VersionItem } from '../types/version';
import { versionService } from '../services/versionService';
import { getLastReadVersion, markAllAsRead } from '../utils/versionStorage';

interface WhatsNewState {
  isOpen: boolean;
  versions: VersionItem[];
  version: string;  // Latest version for display in header
  loading: boolean;
  error: string | undefined;
  shouldShow: boolean;
  openModal: () => void;
  closeModal: () => void;
  dismissPermanently: () => void;
}

/**
 * Hook to manage What's New modal
 * Automatically shows modal when there are unread versions
 */
export function useWhatsNew(): WhatsNewState {
  const [isOpen, setIsOpen] = useState(false);
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [version, setVersion] = useState('unknown');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [shouldShow, setShouldShow] = useState(false);

  // Load version history and check for unread versions
  useEffect(() => {
    const loadChangelog = async () => {
      setLoading(true);
      try {
        // Fetch manifest
        const manifest = await versionService.fetchManifest();
        const lastRead = getLastReadVersion();

        // Store latest version for header display
        setVersion(manifest.latest);

        // Get unread versions
        const unreadVersionStrings = versionService.getUnreadVersions(manifest.versions, lastRead);

        // Limit to 10 most recent
        const versionsToShow = unreadVersionStrings.slice(0, 10);

        // Fetch version data in parallel
        const versionData = await versionService.fetchVersions(versionsToShow);

        // Map to VersionItem with UI state
        const items: VersionItem[] = versionData.map(data => ({
          ...data,
          isUnread: true,
          isLatest: data.version === manifest.latest
        }));

        setVersions(items);
        setShouldShow(items.length > 0);

        // Auto-open if there are unread versions
        if (items.length > 0) {
          setIsOpen(true);
        }
      } catch (err) {
        console.warn('Failed to load version history:', err);
        setError('Failed to load version history');
        setVersion('1.4.1'); // Fallback version
      } finally {
        setLoading(false);
      }
    };

    loadChangelog();
  }, []);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    // Mark all displayed versions as read
    if (versions.length > 0) {
      markAllAsRead(versions);
    }
  };

  const dismissPermanently = () => {
    setIsOpen(false);
    if (versions.length > 0) {
      markAllAsRead(versions);
    }
    setShouldShow(false);
  };

  return {
    isOpen,
    versions,
    version,
    loading,
    error,
    shouldShow,
    openModal,
    closeModal,
    dismissPermanently,
  };
}
